import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Users, GraduationCap, UserPlus, Receipt, Wallet, CheckSquare,
  BookOpen, BarChart3, AlertCircle, FileSignature,
  Activity, CreditCard,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { http } from "../lib/http";
import { PageHeader, PageBody } from "../components/shell/PageChrome";
import AbsenceNotifyCard from "../components/AbsenceNotifyCard";
import { FEATURES } from "../config/features";

const CARD_LIB = {
  activeStudents:      { icon: GraduationCap,  accent: "#0A1F44", to: "/students" },
  activeClasses:       { icon: Users,          accent: "#0A1F44", to: "/classes" },
  activeLeads:         { icon: UserPlus,       accent: "#C8102E", to: "/leads" },
  overdueInvoices:     { icon: Receipt,        accent: "#C8102E", to: "/defaulters" },
  revenuePaid:         { icon: Wallet,         accent: "#0A1F44", to: "/invoices" },
  attendanceRate:      { icon: CheckSquare,    accent: "#0A1F44", to: "/attendance" },
  myClasses:           { icon: BookOpen,       accent: "#0A1F44", to: "/classes" },
  myAttendance:        { icon: CheckSquare,    accent: "#0A1F44", to: "/attendance" },
  myGrades:            { icon: BarChart3,      accent: "#0A1F44", to: "/grades" },
  myChildren:          { icon: GraduationCap,  accent: "#0A1F44", to: "/students" },
  outstandingInvoices: { icon: AlertCircle,    accent: "#C8102E", to: "/invoices" },
  pendingSignatures:   { icon: FileSignature,  accent: "#C8102E", to: "/profile" },
  upcomingSepa:        { icon: CreditCard,     accent: "#0A1F44", to: "/sepa" },
  integrationsHealth:  { icon: Activity,       accent: "#0A1F44", to: "/admin/settings" },
};

const ROLE_CARDS = {
  admin:        ["activeStudents","activeClasses","activeLeads","overdueInvoices","revenuePaid","upcomingSepa","pendingSignatures","integrationsHealth"],
  receptionist: ["activeStudents","activeClasses","activeLeads","overdueInvoices","revenuePaid","upcomingSepa"],
  teacher:      ["activeClasses","attendanceRate","pendingSignatures"],
  parent:       ["myChildren","outstandingInvoices","pendingSignatures"],
  student:      ["myClasses","myAttendance","myGrades"],
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { permissions } = useOutletContext() || {};
  const firstName = (user?.full_name || user?.email || "").split(/[\s@]/)[0];
  const role = user?.role || "student";
  const cards = ROLE_CARDS[role] || ROLE_CARDS.student;

  // Phase 6 metrics — lightweight loads in parallel
  const [pendingSigs, setPendingSigs] = useState(null);
  const [health, setHealth] = useState(null);
  const [overdue, setOverdue] = useState(null);

  useEffect(() => {
    if (cards.includes("pendingSignatures")) {
      http.get("/signatures").then(({data}) => {
        const list = data.items || [];
        const me = list.filter(s => s.status === "pending");
        setPendingSigs(me.length);
      }).catch(() => setPendingSigs(0));
    }
    if (cards.includes("integrationsHealth") && role === "admin") {
      http.get("/admin/integrations/health").then(({data}) => {
        const total = Object.keys(data).length;
        const ok = Object.values(data).filter(v => v.ok).length;
        setHealth(`${ok}/${total}`);
      }).catch(() => setHealth("—"));
    }
    if (cards.includes("overdueInvoices") || cards.includes("outstandingInvoices")) {
      http.get("/defaulters").then(({data}) => setOverdue((data.items || []).length)).catch(() => setOverdue(0));
    }
    // eslint-disable-next-line
  }, [role]);

  const valueFor = (k) => {
    if (k === "pendingSignatures") return pendingSigs;
    if (k === "integrationsHealth") return health;
    if (k === "overdueInvoices" || k === "outstandingInvoices") return overdue;
    return null;
  };

  const filtered = cards.filter((key) => {
    // Feature-flag gate: hide cards belonging to disabled features even for admins.
    if (key === "activeLeads" && !FEATURES.leads) return false;
    // overdueInvoices is the Defaulters-feature landing card (deep-links to
    // /defaulters). When Defaulters is hidden, drop the card entirely so we
    // don't leave a tile that navigates to a redirect target.
    if (key === "overdueInvoices" && !FEATURES.defaulters) return false;
    if (role === "admin") return true;
    const modMap = {
      activeStudents: "students", activeClasses: "classes", activeLeads: "leads",
      overdueInvoices: "invoices", outstandingInvoices: "invoices", revenuePaid: "invoices",
      upcomingSepa: "sepa", attendanceRate: "attendance", myAttendance: "attendance",
      myGrades: "grades", myClasses: "classes", myChildren: "students",
      pendingSignatures: null, integrationsHealth: null,
    };
    const mod = modMap[key];
    if (mod === null || mod === undefined) return true;
    return permissions?.[mod]?.view !== false;
  });

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={t("dashboard.welcome", { name: firstName })}
        subtitle={t(`dashboard.subtitleByRole.${role}`, { defaultValue: t("dashboard.subtitle") })}
      />
      <PageBody>
        {role === "parent" && (
          <div className="mb-6"><AbsenceNotifyCard /></div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dashboard-cards">
          {filtered.map((key) => {
            const { icon: Icon, accent, to } = CARD_LIB[key];
            const v = valueFor(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => to && navigate(to)}
                data-testid={`metric-${key}`}
                className="text-left bg-white rounded-2xl border border-brand-lightGrey p-6 hover:shadow-md hover:border-brand-lightNavy/40 hover:-translate-y-0.5 transition-all duration-200 group focus-visible:ring-2 focus-visible:ring-brand-navy"
              >
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow text-brand-mediumGrey">
                    {t(`dashboard.${key}`)}
                  </div>
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${accent}14`, color: accent }}
                  >
                    <Icon size={16} strokeWidth={1.75} />
                  </div>
                </div>
                <div className="mt-4 text-[2rem] leading-none font-display">
                  {v !== null && v !== undefined ? v : t("dashboard.noData")}
                </div>
                <div className="mt-2 text-xs text-brand-mediumGrey/70">—</div>
              </button>
            );
          })}
        </div>
      </PageBody>
    </div>
  );
}
